import {
  ArrowLeft,
  Clock,
  BookOpen,
  FileText,
  Circle,
  CheckCircle2,
  PlayCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProgressBar } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { getDifficultyColor, getLessonIcon } from '../utils';
import type { LessonProgressEntry } from '../types';

interface CurriculumLesson {
  lessonId: string;
  title: string;
  lessonType: string;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
}

interface CurriculumModule {
  moduleId: string;
  title: string;
  description: string | null;
  sequenceOrder: number;
  estimatedDurationMinutes: number | null;
  lessons: CurriculumLesson[];
}

interface Curriculum {
  courseId: string;
  slug: string;
  title: string;
  description: string | null;
  difficultyLevel: string;
  estimatedDurationHours: number | null;
  tags: string[];
  status: string;
  modules: CurriculumModule[];
}

interface CourseDetailProps {
  curriculum: Curriculum;
  onBack: () => void;
  onStartLesson: (lesson: CurriculumLesson, module: CurriculumModule) => void;
  lessonProgress: Record<string, LessonProgressEntry>;
}

export function CourseDetail({
  curriculum,
  onBack,
  onStartLesson,
  lessonProgress,
}: CourseDetailProps) {
  const totalLessons = curriculum.modules.reduce((acc, m) => acc + m.lessons.length, 0);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Back Button */}
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Courses
      </Button>

      {/* Course Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-2xl font-bold">{curriculum.title}</h1>
          <Badge className={cn('text-xs', getDifficultyColor(curriculum.difficultyLevel))}>
            {curriculum.difficultyLevel}
          </Badge>
        </div>
        <p className="text-muted-foreground mb-4">{curriculum.description}</p>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{curriculum.estimatedDurationHours} hours</span>
          </div>
          <div className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            <span>{curriculum.modules.length} modules</span>
          </div>
          <div className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            <span>{totalLessons} lessons</span>
          </div>
        </div>
      </div>

      {/* Curriculum */}
      <div className="space-y-4">
        {curriculum.modules.map((module, moduleIndex) => {
          const completedLessons = module.lessons.filter(
            (l) => lessonProgress[l.lessonId]?.status === 'completed',
          ).length;
          const modulePercent =
            module.lessons.length > 0
              ? Math.round((completedLessons / module.lessons.length) * 100)
              : 0;

          return (
            <Card key={module.moduleId}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <span className="text-primary font-mono text-sm">{moduleIndex + 1}</span>
                  {module.title}
                </CardTitle>
                <CardDescription>{module.description}</CardDescription>
                {Object.keys(lessonProgress).length > 0 && (
                  <div className="pt-2">
                    <ProgressBar value={modulePercent} className="h-3" />
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {module.lessons.map((lesson) => {
                    const LessonIcon = getLessonIcon(lesson.lessonType);
                    const progress = lessonProgress[lesson.lessonId];
                    const isCompleted = progress?.status === 'completed';

                    return (
                      <div
                        key={lesson.lessonId}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => onStartLesson(lesson, module)}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                        <LessonIcon className="h-4 w-4 text-muted-foreground" />
                        <span className={cn('flex-1', isCompleted && 'text-muted-foreground')}>
                          {lesson.title}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {lesson.estimatedDurationMinutes}min
                        </span>
                        <PlayCircle className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100" />
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
